/* ══════════════════════════════════════════════════════════════════════════
 * WHICH TABS A VENUE SHOWS
 *
 * ⚠️ AL, 22 September 2026: *"Not every restaurant will have all four deals…
 * if they don't put anything in, for example, Game Day, then Game Day should
 * not show up as one of their tabs. Signature Menu will always be there
 * because it's not a deal… whatever they choose or type in, that shows up.
 * The other ones don't."*
 *
 * And on the other two: *"I think we always show the Feeds and Reviews. This
 * way it'll force the restaurant also to put something up… at least we'll be
 * populated everywhere."*
 *
 * ── WHY THIS IS ITS OWN FILE ────────────────────────────────────────────────
 *
 * Because the same question gets asked in more than one place. The venue page
 * asks it to draw a tab strip. Home, Explore and the View All pages ask a
 * version of it to decide which venues belong in which rail. Zaryna asks it
 * when she decides whether a venue can answer "who has a Happy Hour tonight".
 *
 * Four copies of one rule is exactly the fault this repository keeps hitting —
 * the /v1 prefix, the sixteen connector files, the two sign-in doors, the two
 * ways of spelling a venue slug. So the rule lives here once, and anything
 * that needs it imports it.
 *
 * ⚠️ AND IT IS DELIBERATELY NOT A COLUMN. A `does_happy_hour` flag on the
 * venue would be a second copy of what the deals already say, needing to be
 * set, unset and kept true. A venue has a Happy Hour if it has a published
 * Happy Hour. Nothing to maintain, nothing to drift.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE ORDER IS THE LOCKED SCREEN'S ORDER, and it is not alphabetical or
   arbitrary: About, then what the venue serves every day, then the four
   offers, then what other people have said. `11_Restaurant.html` draws them
   in exactly this sequence. */
export const TABS = [
  { key: 'about',   label: 'About' },
  { key: 'menu',    label: 'Signature Menu' },
  { key: 'hh',      label: 'Happy Hour',          deal: 'happy_hour' },
  { key: 'daily',   label: 'Daily Deal',          deal: 'daily' },
  { key: 'lm',      label: 'Last-Minute Special', deal: 'last_minute' },
  { key: 'gd',      label: 'Game Day',            deal: 'game_day' },
  { key: 'feeds',   label: 'Feeds' },
  { key: 'reviews', label: 'Reviews' },
];

/**
 * The tabs this venue shows.
 *
 * @param deals  every live deal for the venue, as /deals/restaurant/:id
 *               returns them. `null` means "still being asked for".
 *
 * ⚠️ WHILE IT IS null, THE DEAL TABS ARE ABSENT — NOT ALL FOUR PRESENT.
 * Drawing four and then removing three is a page that rearranges itself under
 * somebody's thumb, and the tab they were reaching for may be the one that
 * goes. Tabs arrive; they never leave.
 *
 * ⚠️ AND IT IS "HAS ONE", NOT "ON RIGHT NOW". Wing Wednesday keeps its tab on
 * a Sunday — Al: *"Wing Wednesday has always been Wing Wednesday… it just
 * keeps on going on and on."* The tab is there; the tab says when it runs.
 * This is free rather than clever: isLiveNow() on the server checks a deal's
 * overall run and its status and never looks at the weekday, so the ordinary
 * public call already returns Wing Wednesday on a Sunday. The weekly pattern
 * lives in `happy_hour_schedules` and is display data.
 */
/* ⚠️ GAME DAY ALSO COMES FROM THE GAMES — 1 October 2026. A venue whose
   Game Day menu is on and which has a game still to come gets the tab with or
   without a Game Day deal row: 019 made the games THE Game Day model, and
   without this the games a venue is showing could not be reached at all.
   `extra.gameDay` is the server's `has_game_day` (live.sql.js
   gameDayUpcoming) — the same rule that puts the venue on the Home rail. */
export function tabsFor(deals, extra = {}) {
  const has = new Set((deals || []).map((d) => d && d.deal_type));
  if (extra && extra.gameDay) has.add('game_day');
  return TABS.filter((t) => !t.deal || has.has(t.deal));
}

/**
 * The tab to actually open.
 *
 * ⚠️ `?tab=gd` CAN ARRIVE FOR A VENUE WITH NO GAME DAY — typed, shared in a
 * link, or left over from the venue looked at before this one. Without this
 * the page draws a strip that does not contain the selected tab, and a pane
 * with nothing in it, and neither looks like an error to anyone.
 */
export function openTab(deals, wanted, extra = {}) {
  const shown = tabsFor(deals, extra);
  return shown.some((t) => t.key === wanted) ? wanted : 'about';
}
